/**
 * Modale de génération de promesse d'embauche.
 * Phase 4-A du process embauche.
 *
 * Flow 2 étapes :
 *  1. Paramètres   → champs structurés (poste, salaire, dates, etc.)
 *  2. Aperçu       → texte composé pré-rempli, éditable avant génération PDF
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Loader,
  Download,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';
import Modal from '../ui/Modal';
import { generateOfferLetter, previewOfferLetter } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const DEFAULT_TRIAL_MONTHS = 2;
const DEFAULT_REPLY_DAYS = 7;

function addDays(date, days) {
  const d = date ? new Date(date) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function fmtDateInput(d) {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date)) return '';
  return date.toISOString().slice(0, 10);
}

export default function OfferLetterModal({ employee, onClose, onGenerated }) {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bodyText, setBodyText] = useState(''); // Paragraphes séparés par double saut de ligne

  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    civility: employee?.civilite === 'Monsieur' ? 'Monsieur' : 'Madame',
    candidateAddress: employee?.address || '',
    position: employee?.position || '',
    contractType: employee?.contractType || 'CDI',
    timeType: '',
    monthlySalary: employee?.currentSalary ? Math.round(Number(employee.currentSalary)) : '',
    weeklyHours: employee?.workHours ? Number(employee.workHours) : 35,
    startDate: employee?.startDate ? fmtDateInput(employee.startDate) : today,
    condition: '',
    trialMonths: DEFAULT_TRIAL_MONTHS,
    replyDelayDays: DEFAULT_REPLY_DAYS,
    date: today,
    replyDeadline: addDays(today, DEFAULT_REPLY_DAYS),
  });

  useEffect(() => {
    setForm((f) => ({ ...f, replyDeadline: addDays(f.date, Number(f.replyDelayDays) || DEFAULT_REPLY_DAYS) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, form.replyDelayDays]);

  const timeTypeAuto = form.timeType || (Number(form.weeklyHours) >= 35 ? 'à temps plein' : 'à temps partiel');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payloadFromForm = useMemo(() => ({
    ...form,
    timeType: timeTypeAuto,
  }), [form, timeTypeAuto]);

  // Étape 1 → 2 : fetch preview depuis backend, pré-remplit le textarea
  const handleNext = async () => {
    if (!form.position?.trim()) {
      toast.error('Le poste est obligatoire');
      return;
    }
    if (!form.monthlySalary || Number(form.monthlySalary) <= 0) {
      toast.error('Le salaire mensuel est obligatoire');
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await previewOfferLetter(employee.id, payloadFromForm);
      const txt = (data?.bodyParagraphs || []).join('\n\n');
      setBodyText(txt);
      setStep(2);
    } catch (e) {
      console.error('[OfferLetterModal] preview error:', e);
      toast.error(e?.response?.data?.error || 'Erreur lors de la composition du courrier');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Recomposer le texte par défaut (si l'utilisateur veut annuler ses modifs)
  const handleResetBody = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await previewOfferLetter(employee.id, payloadFromForm);
      setBodyText((data?.bodyParagraphs || []).join('\n\n'));
      toast.success('Texte réinitialisé');
    } catch (e) {
      toast.error('Impossible de recomposer le texte');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Split par double saut de ligne → liste de paragraphes
      const bodyParagraphs = bodyText
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);

      const payload = { ...payloadFromForm, bodyParagraphs };
      const { data } = await generateOfferLetter(employee.id, payload);

      if (data?.pdfBase64) {
        const blob = base64ToBlob(data.pdfBase64, 'application/pdf');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.document?.originalFileName || `Promesse d'embauche.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast.success('Promesse générée et ajoutée aux documents');
      onGenerated?.(data?.document);
      onClose();
    } catch (e) {
      console.error('[OfferLetterModal] generate error:', e);
      const msg =
        e?.response?.data?.error ||
        (e?.code === 'ECONNABORTED' ? 'Timeout - la génération PDF est trop lente' : null) ||
        e?.message ||
        'Erreur génération';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const inputCls =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  const busy = previewLoading || generating;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={step === 1 ? "Promesse d'embauche — Paramètres" : "Promesse d'embauche — Aperçu du courrier"}
      size="2xl"
      footer={
        step === 1 ? (
          <>
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleNext}
              disabled={busy}
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-60 flex items-center gap-2"
            >
              {previewLoading ? <Loader size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {previewLoading ? 'Composition...' : 'Suivant : aperçu'}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setStep(1)}
              disabled={busy}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              <ArrowLeft size={14} /> Précédent
            </button>
            <button
              onClick={handleResetBody}
              disabled={busy}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 flex items-center gap-2"
              title="Recomposer le texte à partir des paramètres"
            >
              <RotateCcw size={14} /> Réinitialiser
            </button>
            <button
              onClick={handleGenerate}
              disabled={busy}
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-60 flex items-center gap-2"
            >
              {generating ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
              {generating ? 'Génération...' : 'Générer et télécharger'}
            </button>
          </>
        )
      }
    >
      {step === 1 ? (
        <div className="space-y-5">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
            <FileText size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium mb-0.5">Promesse pour {employee?.firstName} {employee?.lastName}</p>
              <p>Structure : {employee?.structure?.name || '—'}. L'en-tête société utilise l'identité juridique de la structure (Admin &gt; Structures &gt; Identité juridique). Les champs vides utilisent les valeurs par défaut Ô P'tites Quenottes.</p>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Destinataire</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Civilité</label>
                <select value={form.civility} onChange={(e) => set('civility', e.target.value)} className={inputCls}>
                  <option value="Madame">Madame</option>
                  <option value="Monsieur">Monsieur</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Adresse (optionnel)</label>
                <input type="text" value={form.candidateAddress} onChange={(e) => set('candidateAddress', e.target.value)} className={inputCls} placeholder="69100 Villeurbanne" />
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Détails de l'offre</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Poste <span className="text-red-500">*</span></label>
                <input type="text" value={form.position} onChange={(e) => set('position', e.target.value)} className={inputCls} placeholder="auxiliaire petite enfance" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Type de contrat</label>
                <select value={form.contractType} onChange={(e) => set('contractType', e.target.value)} className={inputCls}>
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="Alternance">Alternance</option>
                  <option value="Stage">Stage</option>
                  <option value="Intérimaire">Intérimaire</option>
                  <option value="Prestataire">Prestataire</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Temps de travail</label>
                <select value={form.timeType} onChange={(e) => set('timeType', e.target.value)} className={inputCls}>
                  <option value="">Auto ({Number(form.weeklyHours) >= 35 ? 'à temps plein' : 'à temps partiel'})</option>
                  <option value="à temps plein">à temps plein</option>
                  <option value="à temps partiel">à temps partiel</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Salaire mensuel brut <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="number" min="0" step="50" value={form.monthlySalary} onChange={(e) => set('monthlySalary', e.target.value)} className={inputCls} placeholder="1910" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Heures par semaine</label>
                <input type="number" min="0" max="48" step="0.5" value={form.weeklyHours} onChange={(e) => set('weeklyHours', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date d'entrée en fonction</label>
                <input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Durée période d'essai (mois)</label>
                <input type="number" min="0" max="6" step="1" value={form.trialMonths} onChange={(e) => set('trialMonths', e.target.value)} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Condition (optionnel)</label>
                <input type="text" value={form.condition} onChange={(e) => set('condition', e.target.value)} className={inputCls} placeholder="ex: réception de votre diplôme de CAP Petite Enfance avant cette date" />
                <p className="text-[10px] text-gray-400 mt-1">
                  Apparaît dans la phrase : « La date d'entrée serait le X <strong>sous réserve de [ta condition]</strong> »
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Délai de réponse</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date du document</label>
                <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Délai (jours)</label>
                <input type="number" min="1" max="30" step="1" value={form.replyDelayDays} onChange={(e) => set('replyDelayDays', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date limite (auto)</label>
                <input type="date" value={form.replyDeadline} onChange={(e) => set('replyDeadline', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Étape 2 : aperçu éditable
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
            <FileText size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium mb-0.5">Aperçu du contenu</p>
              <p>Ce texte sera inséré entre l'en-tête (expéditeur, destinataire, date, objet) et les signatures. Vous pouvez le modifier librement avant génération. Séparez les paragraphes par une ligne vide.</p>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Contenu du courrier</label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={18}
              className={`${inputCls} font-mono text-[13px] leading-relaxed`}
              placeholder="Bonjour,\n\nNous avons le plaisir..."
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

function base64ToBlob(b64, contentType) {
  const byteCharacters = atob(b64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}
